/**
 * Retry Utilities
 * Provides retry logic with exponential backoff for API requests
 */

import { API_CONFIG } from 'config/api.config';

/**
 * Interface for retry options
 */
export interface RetryOptions {
  maxRetries?: number;
  retryDelay?: number;
  backoffMultiplier?: number;
  shouldRetry?: (error: any) => boolean;
  onRetry?: (attempt: number, error: any) => void;
}

/**
 * Default function to determine if an error should trigger a retry
 */
const defaultShouldRetry = (error: any): boolean => {
  // Retry on network errors
  if (!error.response && error.message?.includes('fetch')) {
    return true;
  }

  // Retry on timeout errors
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return true;
  }

  // Retry on specific HTTP status codes
  if (error.response) {
    const status = error.response.status;
    // Retry on 408 (Request Timeout), 429 (Too Many Requests), 500, 502, 503, 504
    return [408, 429, 500, 502, 503, 504].includes(status);
  }

  return false;
};

/**
 * Sleep function for delays
 */
const sleep = (ms: number): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, ms));

/**
 * Retry a function with exponential backoff
 * @param fn The function to retry
 * @param options Retry options
 * @returns The result of the function
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxRetries = API_CONFIG.RETRY.MAX_RETRIES,
    retryDelay = API_CONFIG.RETRY.RETRY_DELAY,
    backoffMultiplier = API_CONFIG.RETRY.BACKOFF_MULTIPLIER,
    shouldRetry = defaultShouldRetry,
    onRetry,
  } = options;

  let lastError: any;
  let delay = retryDelay;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // If this was the last attempt or error shouldn't be retried, throw
      if (attempt === maxRetries || !shouldRetry(error)) {
        throw error;
      }

      // Call the onRetry callback if provided
      if (onRetry) {
        onRetry(attempt + 1, error);
      }

      // Log the retry attempt
      console.warn(
        `Request failed (attempt ${attempt + 1}/${maxRetries + 1}). Retrying in ${delay}ms...`,
        error
      );

      // Wait before retrying
      await sleep(delay);

      // Increase delay for next attempt (exponential backoff)
      delay *= backoffMultiplier;
    }
  }

  throw lastError;
}

/**
 * Check if the error is a network error
 */
export const isNetworkError = (error: any): boolean => {
  return (
    !error.response &&
    (error.message?.includes('fetch') ||
      error.message?.includes('network') ||
      error.code === 'ECONNABORTED')
  );
};

/**
 * Get a user-friendly error message
 */
export const getUserFriendlyErrorMessage = (error: any): string => {
  // Network errors
  if (isNetworkError(error)) {
    return 'Unable to connect to the server. Please check your internet connection and try again.';
  }

  // Timeout errors
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return 'Request timed out. Please try again.';
  }

  // HTTP error responses
  if (error.response) {
    const status = error.response.status;
    switch (status) {
      case 400:
        return error.response.data?.message || 'Invalid request. Please check your input.';
      case 401:
        return 'Authentication required. Please log in again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 408:
        return 'Request timed out. Please try again.';
      case 413:
        return 'The file is too large. Please upload a smaller file.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'Server error. Please try again later.';
      case 502:
        return 'Bad gateway. The server is temporarily unavailable.';
      case 503:
        return 'Service temporarily unavailable. Please try again later.';
      case 504:
        return 'Gateway timeout. The server is taking too long to respond.';
      default:
        return error.response.data?.message || 'An unexpected error occurred. Please try again.';
    }
  }

  // Generic error
  return error.message || 'An unexpected error occurred. Please try again.';
};
